import {
    Avatar,
    Button,
    dispatchCustomEvent,
    Flexer,
    Heading,
    Icon,
    If,
    Input,
    InputControl,
    InputPrefix,
    Label,
    Modal,
    ModalClose,
    plural,
    Portal,
    Select,
    SelectOption,
    Stack,
    Text,
    useDialog,
    UserList,
    UserListUser,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TbCircleMinus, TbMinus, TbSquareRoundedMinus, TbTrash, TbUserPlus } from 'react-icons/tb'
import { useParams } from 'react-router'
import { ColorButton, Error, Loading, UserSelectUser } from '../components'
import { COLORS } from '../constants'
import { byName, logger } from '../helpers/util'
import { useTeams, useTeamUsers, useWorkspaceUsers } from '../hooks/api.hooks'
import { useAppStore } from '../store/app'

export const TeamModal = ({ teamId, onDismiss, onDelete = undefined, onUpdate = undefined }) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const { setDialog, closeDialog } = useDialog()
    const { workspaceId } = useParams()
    const [filter, setFilter] = useState('')
    const teamUsersAPI = useTeamUsers()
    const workspaceUsersAPI = useWorkspaceUsers()
    const teamsAPI = useTeams()
    const [users, setUsers] = useState<any>([])
    const [name, setName] = useState('')
    const [color, setColor] = useState('')
    const pageSize = 10
    const teamUser = useRef(null)
    const actions = useAppStore((state) => state.actions)
    const project = useAppStore((state) => state.project)
    const workspace = useAppStore((state) => state.workspace)
    //const isAdmin = useMemo(() => workspace.role == 'Owner' || workspace.role == 'Admin', [workspace.role])
    const isAdmin = useMemo(() => workspace.role != 'Guest', [workspace.role])
    const [pageUsers, setPageUsers] = useState(1)
    const filteredUsers = useMemo(
        () => users.filter((user: any) => user.name.toLowerCase().includes(filter.toLowerCase())),
        [users, filter]
    )
    const [availableUsers, setAvailableUsers] = useState([])
    const selected = useMemo(() => users.map(({ id }) => id), [users.length])
    const options: any = useMemo(() => {
        return availableUsers.map((user: UserSelectUser) => ({
            key: user.id,
            label: user.name,
            prefix: (
                <Avatar
                    key={user.id}
                    src={user.image}
                    name={user.name}
                    size="xs"
                />
            ),
        }))
    }, [availableUsers])

    const handleUserSelect = (option: SelectOption) => {
        const availableUser = availableUsers.find((availableUser: any) => availableUser.id == option.key)
        const user = users.find((user: any) => user.id == option.key)

        if (!user) {
            handleUserAdd(availableUser)
        } else {
            handleUserDelete(user)
        }
    }

    const handleUserFilter = async (query) => {
        setError(null)

        try {
            const users = await workspaceUsersAPI.getSearch(workspaceId, query)
            setAvailableUsers(users.sort(byName))
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleUserAdd = async (user) => {
        const teamUser = await teamUsersAPI.create(workspaceId, teamId, user)
        const { id } = teamUser
        setUsers([...users, { ...user, teamUserId: id }])
        dispatchCustomEvent('team:refresh-preview', { id: teamId })
    }

    const handleUserDelete = async (user) => {
        const { id, teamUserId } = user
        setUsers(users.filter((u) => u.id != id))
        await teamUsersAPI.remove(workspaceId, teamId, teamUserId)
        dispatchCustomEvent('team:refresh-preview', { id: teamId })
    }

    const handleRemoveTeamMember = async (teamUserId) => {
        setDialog({
            title: 'Are you sure?',
            description: 'They will lose access to this team and all the resources.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            teamUsersAPI
                                .remove(workspaceId, teamId, teamUserId)
                                .then(() => {
                                    fetchData()
                                    dispatchCustomEvent('team:refresh-preview', { id: teamId })
                                    closeDialog()
                                })
                                .catch(() => {
                                    closeDialog()
                                })
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleLeave = async () => {
        setDialog({
            title: 'Are you sure?',
            description: 'You will lose access to this team and all the resources.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            teamUsersAPI
                                .remove(workspaceId, teamId, teamUser.current.id)
                                .then(() => {
                                    actions.teamDelete(teamId)
                                    closeDialog()
                                    onDismiss()
                                    if (onDelete) onDelete()
                                })
                                .catch(() => {
                                    closeDialog()
                                    onDismiss()
                                })
                        }}
                        variant="danger">
                        Leave
                    </Button>
                </View>
            ),
        })
    }

    const handleDelete = async () => {
        setDialog({
            title: 'Delete this team?',
            description:
                'Members will still remain part of this workspace, but this team will be dissolved. Are you sure this is correct?',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            teamsAPI
                                .remove(workspaceId, teamId)
                                .then(() => {
                                    actions.teamDelete(teamId)
                                    closeDialog()
                                    onDismiss()
                                    if (onDelete) onDelete()
                                })
                                .catch(() => {
                                    closeDialog()
                                    onDismiss()
                                })
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleSave = async () => {
        setLoading(true)
        setError(null)
        try {
            await teamsAPI.update(workspaceId, teamId, { name, color })

            actions.teamUpdate({ id: teamId, name, color })

            if (onUpdate) onUpdate()

            actions.projectUpdate({
                id: project.id,
                teamName: name,
                teamColor: color,
            })

            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)
        try {
            const team = await teamsAPI.getOne(workspaceId, teamId)
            const users = await teamUsersAPI.getAll(workspaceId, teamId)

            teamUser.current = { id: team.teamUserId, role: team.role }

            setColor(team.color)
            setName(team.name)
            setUsers(users)
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useEffect(() => {
        if (workspaceId && teamId) fetchData()
    }, [workspaceId, teamId])

    return (
        <>
            <Modal
                disableBackgroundDismiss
                width={500}
                height="90%"
                anchor="middle-center"
                portal={Portal}
                onDismiss={onDismiss}
                isVisible={true}
                header={
                    <>
                        <Heading as="h4">Team</Heading>
                        <Flexer />
                        <ModalClose onClick={onDismiss} />
                    </>
                }
                footer={
                    <Stack
                        p={10}
                        spacing={10}
                        width="100%">
                        <Button onClick={onDismiss}>Close</Button>
                        <Button
                            subtle
                            onClick={handleLeave}>
                            Leave Team
                        </Button>
                        <Flexer />
                        <Button
                            variant="danger"
                            outline
                            onClick={handleDelete}
                            disabled={!isAdmin}
                            prefix={<Icon icon={TbTrash} />}>
                            Delete Team
                        </Button>
                        <Button
                            variant="accent"
                            disabled={!isAdmin}
                            onClick={handleSave}>
                            Save
                        </Button>
                    </Stack>
                }>
                <View
                    zIndex={0}
                    width="100%"
                    height="100%"
                    position="absolute"
                    style={{ inset: 0 }}
                    className="f-overflow-y-auto">
                    <Error error={error} />
                    <Loading loading={loading} />

                    <Stack
                        direction="vertical"
                        spacing="0.75rem"
                        p="var(--f-modal-body-padding)">
                        <Label
                            fontWeight={600}
                            size="sm">
                            Team name
                        </Label>

                        <InputControl>
                            <InputPrefix>
                                <ColorButton
                                    color={color}
                                    onChange={setColor}
                                    colors={COLORS}
                                />
                            </InputPrefix>
                            <Input
                                selectOnFocus
                                placeholder="Marketing"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </InputControl>

                        <Label
                            fontWeight={600}
                            size="sm">
                            {users.length} {plural(users.length, 'team member')}
                        </Label>

                        <Select
                            prefix={<Icon icon={TbUserPlus} />}
                            width="100%"
                            noListFocus
                            trapFocus
                            selected={selected}
                            options={options}
                            placeholder="User..."
                            disabled={!isAdmin}
                            customPlaceholder="Add user..."
                            filterDelay={1000}
                            selectListProps={{
                                noOptionsComponent: <Text p="var(--f-select-option-padding)">There are no users.</Text>,
                            }}
                            onFilter={handleUserFilter}
                            onSelect={(option, dismiss, clear) => {
                                clear()
                                handleUserSelect(option)
                            }}
                        />

                        <UserList
                            shadow="none"
                            users={filteredUsers.map(
                                (user: any): UserListUser => ({
                                    id: user.id,
                                    name: user.name,
                                    description: user.title,
                                    image: user.image,
                                    tools: (
                                        <If if={isAdmin}>
                                            <Button
                                                subtle
                                                size="sm"
                                                onClick={() => handleRemoveTeamMember(user.teamUserId)}>
                                                <Icon icon={TbCircleMinus} />
                                            </Button>
                                        </If>
                                    ),
                                })
                            )}
                        />

                        {!filteredUsers.length && <Text p="2rem 0">No users found</Text>}

                        {/* 
                        {!!filteredUsers.length && (
                            <View
                                row
                                width="100%"
                                m="0 auto"
                                p="1rem 0 0 0">
                                <Pagination
                                    width={150}
                                    pageSize={5}
                                    totalCount={filteredUsers.length}
                                    currentPage={pageUsers}
                                    siblingCount={4}
                                    separator="..."
                                    onPageChange={(page) => setPageUsers(page)}
                                />
                            </View>
                        )}
                         */}
                    </Stack>
                </View>
            </Modal>
        </>
    )
}
